import { useEffect, useId, useState, type ReactNode } from 'react';
import { Icono } from './Icono';
import type { NombreIcono } from './iconos';

/**
 * RV Ventana (componentes.md §11): la única ventana emergente, para confirmaciones y formularios cortos (como mucho 2
 * campos). Sube desde abajo y se posa en el centro (440 ms). Tarjeta blanca radio 24, ancho máx. 460, título 20/800,
 * texto 14, botones en una fila: Cancelar (secundario) + acción (rojo, al final). Sin X: cierran Cancelar, Esc y tocar el
 * fondo. Sigue al teclado (visualViewport) y bloquea el scroll de detrás. En móvil, 16 px a cada lado, botones repartidos
 * y centrada sobre la barra inferior sin taparla (`barraInferior`).
 */
interface Props {
    abierta: boolean;
    titulo: string;
    texto?: string;
    children?: ReactNode;
    /** Texto de cancelar ('' = sin botón). */
    cancelar?: string;
    cancelarIcono?: NombreIcono;
    /** Texto de la acción ('' = sin botón). */
    accion?: string;
    accionIcono?: NombreIcono;
    ocupado?: boolean;
    ocupadoTexto?: string;
    inactiva?: boolean;
    ancho?: string;
    movil?: boolean;
    barraInferior?: boolean;
    onAccion?: () => void;
    onCerrar?: () => void;
    /** Idioma de los textos por defecto (G928, como RV Fecha): `en`, «Cancel», «Save», «Saving…». */
    idioma?: 'es' | 'en';
}

function useVistaVisible() {
    const leer = () => {
        if (typeof window === 'undefined') {
            return { alto: 800, arriba: 0 };
        }
        const vv = window.visualViewport;
        return { alto: vv?.height ?? window.innerHeight, arriba: vv?.offsetTop ?? 0 };
    };
    const [vista, setVista] = useState(leer);
    useEffect(() => {
        const vv = window.visualViewport;
        const ajusta = () => {
            setVista(leer());
        };
        if (vv) {
            vv.addEventListener('resize', ajusta);
            vv.addEventListener('scroll', ajusta);
        } else {
            window.addEventListener('resize', ajusta);
        }
        return () => {
            if (vv) {
                vv.removeEventListener('resize', ajusta);
                vv.removeEventListener('scroll', ajusta);
            } else {
                window.removeEventListener('resize', ajusta);
            }
        };
    }, []);
    return vista;
}

export function Ventana({
    abierta,
    titulo,
    texto,
    children,
    idioma = 'es',
    cancelar = idioma === 'en' ? 'Cancel' : 'Cancelar',
    cancelarIcono = 'x',
    accion = idioma === 'en' ? 'Save' : 'Guardar',
    accionIcono = 'check',
    ocupado = false,
    ocupadoTexto = idioma === 'en' ? 'Saving…' : 'Guardando…',
    inactiva = false,
    ancho = '460px',
    movil = false,
    barraInferior = movil,
    onAccion,
    onCerrar,
}: Props) {
    const idTitulo = useId();
    const { alto, arriba } = useVistaVisible();

    useEffect(() => {
        if (!abierta) {
            return;
        }
        const esc = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && !ocupado) {
                onCerrar?.();
            }
        };
        window.addEventListener('keydown', esc);
        document.documentElement.style.overflow = 'hidden';
        return () => {
            window.removeEventListener('keydown', esc);
            document.documentElement.style.overflow = '';
        };
    }, [abierta, ocupado, onCerrar]);

    const libre = barraInferior ? 100 : 0;
    const centro = arriba + (alto - libre) / 2;
    const cerrar = () => {
        if (!ocupado) {
            onCerrar?.();
        }
    };
    const flex = movil ? '1 1 0' : '0 0 auto';
    const altoBoton = movil ? 48 : 44;

    return (
        <div>
            <div aria-hidden="true" className="rv-ventana__velo" onClick={cerrar} style={{ bottom: barraInferior ? 'calc(100px + env(safe-area-inset-bottom))' : 0, opacity: abierta ? 1 : 0, pointerEvents: abierta ? 'auto' : 'none' }} />
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby={idTitulo}
                aria-hidden={!abierta}
                className="rv-ventana"
                style={{
                    top: centro,
                    width: movil ? 'calc(100vw - 32px)' : `min(${ancho}, calc(100vw - 32px))`,
                    maxHeight: `calc(${String(alto - libre)}px - 32px)`,
                    padding: movil ? '22px 20px 20px' : '26px 26px 24px',
                    opacity: abierta ? 1 : 0,
                    pointerEvents: abierta ? 'auto' : 'none',
                    visibility: abierta ? 'visible' : 'hidden',
                    transform: abierta ? 'translate(-50%, -50%)' : `translate(-50%, ${String(Math.round(alto / 2))}px)`,
                    ['--rv-v-retardo' as string]: abierta ? '0s' : '440ms',
                }}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <span id={idTitulo} className="rv-ventana__titulo">
                        {titulo}
                    </span>
                    {texto !== undefined && texto !== '' && <span className="rv-ventana__texto">{texto}</span>}
                </div>
                {children !== undefined && <div className="rv-ventana__cuerpo">{children}</div>}
                <div className="rv-ventana__botones">
                    {cancelar !== '' && (
                        <button type="button" className="rv-b rv-b-sec" style={{ flex, minHeight: altoBoton }} onClick={cerrar}>
                            <Icono nombre={cancelarIcono} />
                            <span>{cancelar}</span>
                        </button>
                    )}
                    {accion !== '' && (
                        <button
                            type="button"
                            className="rv-b rv-b-pri"
                            style={{ flex, minHeight: altoBoton, opacity: inactiva ? 0.45 : 1 }}
                            disabled={inactiva || ocupado}
                            aria-busy={ocupado}
                            onClick={() => {
                                if (!ocupado && !inactiva) {
                                    onAccion?.();
                                }
                            }}
                        >
                            {ocupado ? <Icono nombre="loader-circle" tamano={18} className="rv-gira" /> : <Icono nombre={accionIcono} />}
                            <span>{ocupado ? ocupadoTexto : accion}</span>
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
